// Evidence-based, rule-based clinical decision support for the GP report.
// First-line investigations reflect common UK/NICE-aligned practice.
// Reference ranges are TYPICAL adult ranges — the reporting lab's own range always takes precedence.
// This is decision support to discuss with a clinician, NOT a diagnosis.

export const SUGGESTED_TESTS = {
  "Rheumatoid Arthritis": ["Rheumatoid factor (RF)", "Anti-CCP antibodies", "ESR", "CRP", "X-ray of affected joints"],
  "Systemic Lupus (SLE)": ["ANA", "Anti-dsDNA", "Complement C3/C4", "Full blood count", "Urinalysis (protein/blood)", "ESR"],
  "Multiple Sclerosis": ["MRI brain & spinal cord", "Neurology referral", "Visual evoked potentials"],
  "Type 1 Diabetes": ["Fasting glucose", "HbA1c", "GAD / islet-cell antibodies", "Urine/blood ketones"],
  "Hashimoto's Thyroiditis": ["TSH", "Free T4", "Anti-TPO antibodies"],
  "Graves' Disease": ["TSH", "Free T4 & Free T3", "TSH-receptor antibodies (TRAb)"],
  "Psoriasis": ["Clinical assessment", "Dermatology referral"],
  "Psoriatic Arthritis": ["ESR", "CRP", "X-ray of affected joints", "Rheumatology referral"],
  "Ankylosing Spondylitis": ["ESR", "CRP", "HLA-B27", "MRI of sacroiliac joints"],
  "Crohn's Disease": ["Full blood count", "CRP", "Ferritin", "Faecal calprotectin", "Gastroenterology referral (endoscopy)"],
  "Ulcerative Colitis": ["Full blood count", "CRP", "Faecal calprotectin", "Gastroenterology referral (colonoscopy)"],
  "Coeliac Disease": ["Anti-tTG IgA", "Total IgA", "Endoscopy/biopsy referral if positive"],
  "Sjögren's Syndrome": ["ANA", "Anti-Ro (SSA) / Anti-La (SSB)", "Schirmer's tear test", "Salivary flow assessment"],
  "Systemic Sclerosis (Scleroderma)": ["ANA", "Anti-Scl-70", "Anti-centromere antibodies", "Nailfold capillaroscopy"],
  "Vasculitis": ["ANCA (MPO/PR3)", "ESR", "CRP", "Urinalysis", "Full blood count"],
  "Myasthenia Gravis": ["Anti-AChR antibodies", "Anti-MuSK antibodies", "Repetitive nerve stimulation / EMG"],
  "Vitiligo": ["Thyroid function tests", "Dermatology assessment"],
  "Alopecia Areata": ["Thyroid function tests", "Ferritin", "Dermatology assessment"],
  "Addison's Disease": ["9am serum cortisol", "ACTH", "Urea & electrolytes", "Short Synacthen test"],
  "Pernicious Anaemia": ["Full blood count", "Vitamin B12", "Intrinsic factor antibodies"],
  "Polymyalgia Rheumatica": ["ESR", "CRP", "Full blood count"],
  "Giant Cell Arteritis": ["ESR", "CRP", "Temporal artery ultrasound/biopsy (urgent)"],
  "Dermatomyositis": ["Creatine kinase (CK)", "Myositis-specific antibodies", "ANA", "EMG"],
  "Raynaud's Phenomenon": ["ANA", "ESR", "Nailfold capillaroscopy"],
  "Autoimmune Hepatitis": ["Liver function tests", "ANA", "Anti-smooth muscle antibody", "Serum IgG"],
  "Sarcoidosis": ["Chest X-ray", "Serum ACE", "Serum calcium", "CT chest"],
  "Behçet's Disease": ["ESR / CRP", "Pathergy test", "Clinical (ICBD) criteria"],
  "Guillain-Barré Syndrome": ["Nerve conduction studies", "Lumbar puncture", "Urgent neurology referral"],
  "Juvenile Idiopathic Arthritis": ["ESR", "CRP", "ANA", "Rheumatoid factor", "Paediatric rheumatology referral"],
  "Mixed Connective Tissue Disease": ["ANA", "Anti-U1-RNP antibodies", "ESR / CRP"],
  "Fibromyalgia (associated)": ["Clinical assessment", "Tests to exclude other causes (TFTs, FBC, ESR, vitamin D)"],
  "Antiphospholipid Syndrome": ["Anticardiolipin antibodies", "Lupus anticoagulant", "Anti-β2-glycoprotein I"],
};

const DEFAULT_TESTS = ["Full blood count", "ESR", "CRP", "Thyroid function tests", "ANA"];

// name-keyword -> typical adult reference range. low/high optional. unit for display.
const REFERENCE_RANGES = [
  { keys: ["hba1c"], low: null, high: 47, unit: "mmol/mol", label: "HbA1c", note: "≥48 is in the diabetes range" },
  { keys: ["crp", "c-reactive"], high: 5, unit: "mg/L", label: "CRP" },
  { keys: ["esr", "sed rate"], high: 20, unit: "mm/hr", label: "ESR", note: "adjust for age/sex" },
  { keys: ["tsh"], low: 0.4, high: 4.0, unit: "mIU/L", label: "TSH" },
  { keys: ["free t4", "ft4"], low: 9, high: 25, unit: "pmol/L", label: "Free T4" },
  { keys: ["ferritin"], low: 30, high: 300, unit: "µg/L", label: "Ferritin" },
  { keys: ["b12", "cobalamin"], low: 200, unit: "ng/L", label: "Vitamin B12" },
  { keys: ["vitamin d", "25-oh", "25 oh"], low: 50, unit: "nmol/L", label: "Vitamin D" },
  { keys: ["haemoglobin", "hemoglobin", "hgb", "hb"], low: 120, high: 170, unit: "g/L", label: "Haemoglobin" },
  { keys: ["platelet", "plt"], low: 150, high: 400, unit: "x10⁹/L", label: "Platelets" },
  { keys: ["white cell", "wcc", "wbc", "leucocyte"], low: 4.0, high: 11.0, unit: "x10⁹/L", label: "White cells" },
  { keys: ["alt", "alanine"], high: 40, unit: "U/L", label: "ALT" },
  { keys: ["creatinine"], low: 60, high: 110, unit: "µmol/L", label: "Creatinine" },
  { keys: ["egfr"], low: 60, unit: "mL/min", label: "eGFR" },
  { keys: ["fasting glucose", "glucose"], low: 3.9, high: 5.5, unit: "mmol/L", label: "Fasting glucose" },
  { keys: ["vitamin b9", "folate"], low: 3.0, unit: "µg/L", label: "Folate" },
];

function matchRange(name) {
  const n = (name || "").toLowerCase();
  // longer/more specific keys first
  for (const r of REFERENCE_RANGES) {
    if (r.keys.some((k) => n.includes(k))) return r;
  }
  return null;
}

export function suggestedTestsFor(conditionNames = []) {
  const seen = new Set();
  const out = [];
  conditionNames.forEach((name) => {
    (SUGGESTED_TESTS[name] || []).forEach((t) => {
      if (!seen.has(t)) { seen.add(t); out.push(t); }
    });
  });
  if (!out.length) DEFAULT_TESTS.forEach((t) => { if (!seen.has(t)) { seen.add(t); out.push(t); } });
  return out;
}

// markers: { name: [ {date, value, unit} ... sorted asc ] }
export function analyseMarkers(markers = {}) {
  const flagged = [];
  Object.keys(markers).forEach((name) => {
    const series = markers[name] || [];
    if (!series.length) return;
    const latest = series[series.length - 1];
    const val = Number(latest.value);
    if (Number.isNaN(val)) return;
    const r = matchRange(name);
    let status = "Within typical range";
    if (r) {
      if (r.high != null && val > r.high) status = "Above typical range";
      else if (r.low != null && val < r.low) status = "Below typical range";
    } else {
      status = "No standard range";
    }
    let trend = "";
    if (series.length > 1) {
      const prev = Number(series[series.length - 2].value);
      if (!Number.isNaN(prev)) trend = val > prev ? "rising" : val < prev ? "falling" : "stable";
    }
    const rangeStr = r
      ? `typical ${r.low != null ? r.low : "–"}${r.high != null ? `–${r.high}` : "+"} ${r.unit}${r.note ? ` (${r.note})` : ""}`
      : "";
    flagged.push({ name, value: latest.value, unit: latest.unit || "", date: latest.date, status, trend, rangeStr, abnormal: status.includes("typical range") && status !== "Within typical range" });
  });
  // abnormal first
  return flagged.sort((a, b) => (b.abnormal ? 1 : 0) - (a.abnormal ? 1 : 0));
}
